import React, { useEffect, useState } from "react"

import { faFileLines } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { OneVerifyEraUploadFileFormat } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import {
    EraUploadIntegrationTab,
    INTEGRATIONS_CONFIG,
} from "../../config/integrations.config"
import { MarkTileElement } from "../../elements/markTile.element"
import { EraUploadHelper } from "../../helpers/eraUpload.helper"
import type { IOneVerifyIntegration } from "../../types/integrations.types"
import type { IEraUploadBatchReviewState } from "../../types/integrations.types"
import { EraUploadCsvAccessRequestComponent } from "./eraUpload/eraUploadCsvAccessRequest.component"
import { EraUploadFileDropzoneComponent } from "./eraUpload/eraUploadFileDropzone.component"
import { EraUploadFormatTabsComponent } from "./eraUpload/eraUploadFormatTabs.component"
import { EraUploadStatusTabComponent } from "./eraUpload/eraUploadStatusTab.component"

import downloadWhiteImg from "@front-shared/assets/images/icons/download-white.svg"

export const UploadEraIntegrationDetailComponent: React.FunctionComponent<
    IUploadEraIntegrationDetailComponentProps
> = props => {
    const [activeTab, setActiveTab] = useState<EraUploadIntegrationTab>(
        () => props.initialTab ?? EraUploadIntegrationTab.x12,
    )

    useEffect(() => {
        if (props.initialTab) {
            setActiveTab(props.initialTab)
        }
    }, [props.initialTab])

    if (!props.integration) {
        return null
    }

    const onDownloadTemplate = () => {
        window.open(
            INTEGRATIONS_CONFIG.eraUpload.csvTemplateUrl,
            "_blank",
            "noopener,noreferrer",
        )
    }

    const isCsvTabActive = activeTab === EraUploadIntegrationTab.csv

    const isUploadedFilesTabActive =
        activeTab === EraUploadIntegrationTab.uploadedFiles

    return (
        <div
            data-produktly="integrations-detail"
            className={`
                rounded-[8px] p-[24px] md:p-[32px]
                border border-solid border-brand-warmLight2 bg-brand-white
                ${props.className ?? ""}
            `}
        >
            <div className="flex gap-[16px] items-start min-w-0">
                <MarkTileElement
                    icon={props.integration.markIcon}
                    backgroundColor={props.integration.markBackgroundColor}
                    markImageUrl={props.integration.markImageUrl}
                    markImageFit={props.integration.markImageFit}
                    tileLabel={props.integration.name}
                    size="large"
                />

                <div className="min-w-0">
                    <PrimaryText typography="h4" weight="semibold">
                        {props.integration.name}
                    </PrimaryText>

                    <PrimaryText
                        className="mt-[8px] block max-w-[640px]"
                        weight="medium"
                        color={NIRVANA_COLORS.brand.grey7}
                    >
                        {props.integration.tagline}
                    </PrimaryText>
                </div>
            </div>

            <div className="my-[24px] h-[1px] bg-brand-warmLight2" />

            {typeof props.integration.description === "string" ? (
                <PrimaryText
                    className="max-w-[720px] mb-[24px]"
                    weight="medium"
                >
                    {props.integration.description}
                </PrimaryText>
            ) : (
                props.integration.description
            )}

            {props.integration.features?.length ? (
                <ul
                    className="
                        grid grid-flow-col grid-cols-2 gap-x-[16px] gap-y-[12px] md:gap-x-[32px]
                        list-none p-0 m-0 mb-[32px]
                        sm:grid-cols-1 sm:grid-flow-row sm:[grid-template-rows:none]
                    "
                    style={{
                        gridTemplateRows: `repeat(${Math.ceil(props.integration.features.length / 2)}, minmax(0, auto))`,
                    }}
                >
                    {props.integration.features.map(feature => {
                        return (
                            <li
                                key={feature}
                                className="min-w-0 flex gap-[10px] items-start"
                            >
                                <span
                                    aria-hidden
                                    className="
                                        mt-[8px] shrink-0 w-[6px] h-[6px] rounded-full bg-brand-blue
                                    "
                                />
                                <PrimaryText
                                    className="min-w-0"
                                    weight="medium"
                                >
                                    {feature}
                                </PrimaryText>
                            </li>
                        )
                    })}
                </ul>
            ) : null}

            <EraUploadFormatTabsComponent
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />

            {isUploadedFilesTabActive ? (
                <EraUploadStatusTabComponent />
            ) : (
                <div className="mt-[24px]">
                    {isCsvTabActive ? (
                        props.isCsvUploadEnabled ? (
                            <>
                                <div
                                    className="
                                        flex items-center justify-between gap-[16px]
                                        rounded-[8px] px-[16px] py-[14px]
                                        bg-brand-lilacLight mb-[16px]
                                        border border-solid border-brand-warmLight2
                                    "
                                >
                                    <div className="flex items-center gap-[12px] min-w-0">
                                        <div
                                            className="
                                                shrink-0 w-[40px] h-[40px] rounded-[8px]
                                                bg-brand-primary flex items-center justify-center
                                            "
                                            aria-hidden
                                        >
                                            <FontAwesomeIcon
                                                icon={faFileLines}
                                                style={{
                                                    width: 18,
                                                    height: 18,
                                                    color: NIRVANA_COLORS.brand
                                                        .white,
                                                }}
                                            />
                                        </div>

                                        <div className="min-w-0">
                                            <PrimaryText typography="subtitleSemibold">
                                                Download template
                                            </PrimaryText>

                                            <PrimaryText
                                                className="mt-[2px]"
                                                size={13}
                                                lineHeight={18}
                                                color={
                                                    NIRVANA_COLORS.brand.grey7
                                                }
                                            >
                                                Pre-formatted CSV with all
                                                required columns
                                            </PrimaryText>
                                        </div>
                                    </div>

                                    <ButtonElement
                                        className="shrink-0"
                                        type="primary"
                                        htmlType="button"
                                        label="Download .csv"
                                        icon={downloadWhiteImg}
                                        buttonClassName="!pl-[16px] !pr-[16px] whitespace-nowrap"
                                        onClick={onDownloadTemplate}
                                    />
                                </div>

                                <EraUploadFileDropzoneComponent
                                    acceptedFileFormat={
                                        OneVerifyEraUploadFileFormat.csv
                                    }
                                    onFilesValidated={files => {
                                        props.onValidationDone(
                                            EraUploadHelper.createBatchReviewState(
                                                OneVerifyEraUploadFileFormat.csv,
                                                files,
                                            ),
                                        )
                                    }}
                                />
                            </>
                        ) : (
                            <EraUploadCsvAccessRequestComponent />
                        )
                    ) : (
                        <EraUploadFileDropzoneComponent
                            acceptedFileFormat={
                                OneVerifyEraUploadFileFormat.x12
                            }
                            onFilesValidated={files => {
                                props.onValidationDone(
                                    EraUploadHelper.createBatchReviewState(
                                        OneVerifyEraUploadFileFormat.x12,
                                        files,
                                    ),
                                )
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

interface IUploadEraIntegrationDetailComponentProps {
    className?: string
    integration: IOneVerifyIntegration
    isCsvUploadEnabled: boolean
    initialTab?: EraUploadIntegrationTab
    onValidationDone: (data: IEraUploadBatchReviewState) => void
}
